import { API } from "@swivl-mono/api-helper";
import { ObjectHelper, warn } from "@swivl-mono/helpers";
import { AppType, AvailableMembersMap, MemberInterface, MemberInviteInterface, MemberInviteStatus, RequireOnly } from "@swivl-mono/types";
import { create } from "zustand";

import AccountModel from "../Account/AccountModel";
import CallModel from "../CallModel/Call.Model";
import { LocalStorageHelper } from "../LocalStorage/LocalStorageHelper";
import { Member } from "./Member.Current";

interface StoreInterface {
  members?: MemberInterface[];
  membersMap?: { [memberId: number]: MemberInterface };
  invites?: MemberInviteInterface[];

  availableMembers: AvailableMembersMap;
  isAvailable: boolean;
}

const BlankState: StoreInterface = {
  members: undefined,
  invites: undefined,
  availableMembers: {},
  isAvailable: false,
};

export default class MembersModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = (state: Partial<StoreInterface>) => {
    MembersModel.useState.setState(state);
  };

  static interval = setInterval(async () => {
    await MembersModel.sendAvailabilityIfNeeded();
  }, 10000);

  static get state() {
    return MembersModel.useState.getState();
  }

  static reset() {
    MembersModel.setState({ ...BlankState });
  }

  static async loadMembers() {
    console.log("Loading members for orgId", Member.orgId);
    API.Members.loadMembers(Member.orgId!)
      .then((members) => {
        const membersMap = {};
        members.forEach((m) => {
          membersMap[m.id] = m;
        });
        console.log("membersMap :", membersMap);

        MembersModel.setState({
          membersMap,
          members: members.sort((m1, m2) => {
            return m1.account.lastName.localeCompare(m2.account.lastName);
          }),
        });
      })
      .catch((error) => {
        warn("Error loading members", error);
      });
  }

  static async updateMember(memberToUpdate: RequireOnly<MemberInterface, "id">, supressSave?: boolean) {
    let membersMap = MembersModel.state.membersMap ? { ...MembersModel.state.membersMap } : {};
    const isMe = memberToUpdate.id === Member.id;

    let member = isMe ? Member.member : membersMap[memberToUpdate.id];
    if (!member) {
      warn("Member not found in state", membersMap, memberToUpdate, memberToUpdate.id, membersMap[memberToUpdate.id]);
      return;
    }

    member = Object.assign({}, member, memberToUpdate);

    if (!supressSave) {
      if (isMe) {
        this.saveAccountMember(member);
      }
    }

    membersMap[member.id] = member;
    MembersModel.setState({ members: Object.values(membersMap), membersMap });
    if (supressSave) {
      return;
    }
    let newMember: Partial<MemberInterface> = { ...member };
    delete newMember.createdAt;
    delete newMember.updatedAt;
    delete newMember.org;
    delete newMember.account;
    delete (newMember as any).jwt;

    try {
      const updateMember = isMe
        ? await API.Members.updateMyMember(Member.orgId!, member.id, ObjectHelper.stripNulls(newMember as any))
        : await API.Members.updateMember(Member.orgId!, member.id, ObjectHelper.stripNulls(newMember as any));
      console.log("updateMember :", updateMember);

      console.log("Updated member post promise", member);
      membersMap[updateMember.id] = updateMember;

      // if (isMe) {
      //   this.saveAccountMember(member);
      // }
      membersMap[member.id] = updateMember;
      MembersModel.setState({ members: Object.values(membersMap), membersMap });
    } catch (error) {
      warn("Error updating member", error);
    }
  }

  static saveAccountMember(memberToUpdate: RequireOnly<MemberInterface, "id">) {
    let accountMember = AccountModel.state.member;
    if (accountMember && accountMember.id === memberToUpdate.id) {
      accountMember = Object.assign({}, accountMember, memberToUpdate);
      AccountModel.setState({ member: accountMember });
      LocalStorageHelper.setItem("member", JSON.stringify(accountMember));
    }
  }

  static async addNewRoleToMember(memberId: number) {
    return API.Members.addRoleToMember(Member.orgId!, memberId)
      .then((member) => {
        this.updateMember(member, true);
      })
      .catch((error) => {
        warn("Error adding role to member", error);
      });
  }

  static async loadInvites() {
    return API.MemberInvite.loadInvites(Member.orgId!)
      .then((invites) => {
        MembersModel.setState({ invites: invites });
      })
      .catch((error) => {
        warn("Error loading invites", error);
      });
  }

  static updateInvite(invite: MemberInviteInterface) {
    let invites = MembersModel.state.invites ? [...MembersModel.state.invites] : [];
    let index = invites.findIndex((m) => m.id === invite.id);

    if (index >= 0) {
      invites[index] = invite;
    } else {
      invites.push(invite);
    }

    MembersModel.setState({ invites: invites });

    const strippedInvited: Partial<MemberInviteInterface> = {
      id: invite.id,
      firstName: invite.firstName.trim(),
      lastName: invite.lastName.trim(),
      email: invite.email.trim(),
      status: invite.status,
      permissions: invite.permissions,
    };

    return API.MemberInvite.updateInvite(Member.orgId!, invite.id, ObjectHelper.stripNulls(strippedInvited as any))
      .then((invite) => {
        let invites = MembersModel.state.invites ? [...MembersModel.state.invites] : [];
        let index = invites.findIndex((m) => m.id === invite.id);
        if (index >= 0) {
          invites[index] = invite;
        } else {
          invites.push(invite);
        }

        MembersModel.setState({ invites: invites });
      })
      .catch((error) => {
        warn("Error updating member", error);
      });
  }

  static createInvite(invite: Partial<MemberInviteInterface>) {
    invite.firstName = invite.firstName?.trim();
    invite.lastName = invite.lastName?.trim();
    invite.email = invite.email?.trim();
    return API.MemberInvite.createInvite(Member.orgId!, invite)
      .then((invite) => {
        let invites = MembersModel.state.invites ? MembersModel.state.invites : [];
        invites.push(invite);
        MembersModel.setState({ invites: invites });
        return invite;
      })
      .catch((error) => {
        warn("Error updating member", error);
        throw error;
      });
  }

  static deleteInvite(invite: Partial<MemberInviteInterface>) {
    return API.MemberInvite.denyOrRevokeInvite(Member.orgId!, invite.uuid!, MemberInviteStatus.REVOKED)
      .then((deleted: MemberInviteInterface) => {
        if (deleted.status === MemberInviteStatus.REVOKED) {
          let invites = MembersModel.state.invites ? MembersModel.state.invites : [];
          let index = invites.findIndex((m) => m.id === invite.id);
          invites.splice(index, 1);
          MembersModel.setState({ invites: invites });
        } else {
          warn("Error deleting invite", deleted);
        }
        return deleted;
      })
      .catch((error) => {
        warn("Error updating member", error);
        throw error;
      });
  }

  static resendInvite(invite: Partial<MemberInviteInterface>) {
    return API.MemberInvite.resendInvite(Member.orgId!, invite.uuid!).catch((error) => {
      warn("Error resendInvite for member", error);
      throw error;
    });
  }

  static async deleteMember(memberId: number) {
    return API.Members.deleteMember(Member.orgId!, memberId)
      .then((_) => {
        const state = { ...MembersModel.state };
        let members = state.members;
        let membersMap = state.membersMap;
        if (!members) {
          return;
        }
        if (membersMap) {
          delete membersMap[memberId];
        }
        let index = members.findIndex((m) => m.id === memberId);
        members.splice(index, 1);
        MembersModel.setState({ members, membersMap });
      })
      .catch((error) => {
        warn("Error deleting member", error);
        throw error;
      });
  }

  static sendAvailabilityIfNeeded() {
    if (Member.id) {
      API.Members.setMemberAvailability(Member.orgId, Member.id, MembersModel.state.isAvailable);
    }
  }
  static setAvailability(available: boolean) {
    MembersModel.setState({ isAvailable: available });
    if (Member.id) {
      API.Members.setMemberAvailability(Member.orgId, Member.id, MembersModel.state.isAvailable);
    }
    if (available) {
      API.Apps.getAppByTypeForLocation(AppType.TWILIO, Member.orgId!).then((twilioApp) => {
        if (!twilioApp) {
          warn("No Twilio app found, cannot generate new token");
          return;
        }
        CallModel.generateMemberTwilioToken(twilioApp, { force: true });
      });
    } else {
      CallModel.clearTwilioToken();
    }
  }
  static memberAvailabilityUpdated(availableMembers: AvailableMembersMap) {
    MembersModel.setState({ availableMembers: availableMembers });
  }
  static getMembersAvailability() {
    return API.Members.getAvailableMembers(Member.orgId!)
      .then((availableMembers) => {
        MembersModel.setState({ availableMembers: availableMembers });
      })
      .catch((error) => {
        warn("Error getting members availability", error);
      });
  }
}
